import {
  createBuyerProfileAppBuyerProfilesPostMutation,
  deleteBuyerProfileAppBuyerProfilesTagIdDeleteMutation,
  listBuyerProfilesAppBuyerProfilesGetOptions,
  listBuyerProfilesAppBuyerProfilesGetQueryKey,
  updateBuyerProfileAppBuyerProfilesTagIdPatchMutation,
} from '@/client/@tanstack/react-query.gen'
import type { BuyerProfileReturn } from '@/client/types.gen'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useSettingsSectionViewed } from '@/hooks/use-analytics'
import { useToast } from '@/hooks/use-toast'
import { parseApiError } from '@/lib/parse-api-error'
import { getTagColorStyle } from '@/lib/tag-color'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Loader2, Pencil, Plus, Tag, Trash2 } from 'lucide-react'
import { useCallback, useState } from 'react'
import { SettingsPageSkeleton } from './-components/skeleton'

export const Route = createFileRoute('/_authed/_user/settings/tags')({
  component: TagsSettingsPage,
  pendingComponent: SettingsPageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Tags',
    getDescription: () => 'Manage tags used to categorize accounts',
  }),
})

function CreateTagDialog({ onSuccess }: { onSuccess: () => void }) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')

  const resetForm = () => {
    setName('')
  }

  const { mutate: createTag, isPending } = useMutation({
    ...createBuyerProfileAppBuyerProfilesPostMutation(),
    onSuccess: () => {
      toast({
        title: 'Tag created',
        description: 'The new tag has been created.',
        variant: 'success',
      })
      setOpen(false)
      resetForm()
      onSuccess()
    },
    onError: (error) => {
      toast({
        title: 'Failed to create tag',
        description: parseApiError(error),
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createTag({
      body: { profileName: name.trim() },
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Create Tag
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create a new tag</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="tag-name">Name</Label>
              <Input
                id="tag-name"
                placeholder="e.g. High Priority, Aerospace, West Coast"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function RenameTagDialog({
  tag,
  onSuccess,
}: {
  tag: BuyerProfileReturn
  onSuccess: () => void
}) {
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(tag.profileName)

  const { mutate: renameTag, isPending } = useMutation({
    ...updateBuyerProfileAppBuyerProfilesTagIdPatchMutation(),
    onSuccess: () => {
      toast({
        title: 'Tag renamed',
        description: 'The tag has been renamed.',
        variant: 'success',
      })
      setOpen(false)
      onSuccess()
    },
    onError: (error) => {
      toast({
        title: 'Failed to rename tag',
        description: parseApiError(error),
        variant: 'destructive',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (trimmed && trimmed !== tag.profileName) {
      renameTag({
        path: { tag_id: tag.id },
        body: { profileName: trimmed },
      })
    }
  }

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) {
      setName(tag.profileName)
    }
  }, [tag.profileName])

  const hasChanged = name.trim() !== tag.profileName && name.trim().length > 0

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Rename tag</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="rename-tag">Name</Label>
              <Input
                id="rename-tag"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !hasChanged}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function DeleteTagButton({
  tag,
  onSuccess,
}: {
  tag: BuyerProfileReturn
  onSuccess: () => void
}) {
  const { toast } = useToast()
  const [showConfirm, setShowConfirm] = useState(false)

  const { mutate: deleteTag, isPending } = useMutation({
    ...deleteBuyerProfileAppBuyerProfilesTagIdDeleteMutation(),
    onSuccess: () => {
      toast({
        title: 'Tag deleted',
        description: `"${tag.profileName}" has been deleted.`,
        variant: 'success',
      })
      setShowConfirm(false)
      onSuccess()
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete tag',
        description: parseApiError(error),
        variant: 'destructive',
      })
    },
  })

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
        onClick={() => setShowConfirm(true)}
      >
        <Trash2 className="h-4 w-4" />
      </Button>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete
              {' '}
              <span className="font-semibold text-foreground">
                "
                {tag.profileName}
                "
              </span>
              ? This will remove the tag from all accounts it is applied to. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={isPending}
              onClick={(e) => {
                e.preventDefault()
                deleteTag({ path: { tag_id: tag.id } })
              }}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
            >
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function TagsSettingsPage() {
  useSettingsSectionViewed('tags')
  const queryClient = useQueryClient()

  const { data: tags = [], isPending: isLoading } = useQuery({
    ...listBuyerProfilesAppBuyerProfilesGetOptions(),
  })

  // Only show tags owned by the current org
  const ownTags = tags.filter(tag => tag.isOwnOrg !== false)

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: listBuyerProfilesAppBuyerProfilesGetQueryKey(),
    })
  }, [queryClient])

  if (isLoading) {
    return <SettingsPageSkeleton />
  }

  return (
    <div className="space-y-4">
      {/* Header with create button */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Tags help you categorize and filter accounts. Create, rename, or delete tags here.
          </p>
        </div>
        <CreateTagDialog onSuccess={handleRefresh} />
      </div>

      {/* Tags table */}
      {ownTags.length === 0
        ? (
            <div className="rounded-md border bg-card p-12 text-center">
              <Tag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No tags yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first tag to start categorizing accounts.
              </p>
            </div>
          )
        : (
            <div className="rounded-md border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tag</TableHead>
                    <TableHead className="w-32 text-right">Accounts</TableHead>
                    <TableHead className="w-24 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ownTags.map(tag => (
                    <TableRow key={tag.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full shrink-0"
                            style={getTagColorStyle(tag.id)}
                          />
                          <span className="font-medium">{tag.profileName}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {tag.accountCount}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <RenameTagDialog tag={tag} onSuccess={handleRefresh} />
                          <DeleteTagButton tag={tag} onSuccess={handleRefresh} />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
    </div>
  )
}
